import React, { useState, useEffect, useRef } from 'react';
import { databaseService } from '../../services/databaseService';
import styles from './Dashboard.module.css';

const HighlightText = ({ text, query }) => {
  if (!query || !query.trim() || !text) return <>{text}</>;
  const str = String(text);
  const idx = str.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{str}</>;
  return <>{str.substring(0, idx)}<mark style={{ background: '#fef08a', padding: '0 1px', borderRadius: 2 }}>{str.substring(idx, idx + query.length)}</mark>{str.substring(idx + query.length)}</>;
};

const DatabaseViewer = () => {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('public');
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableStructure, setTableStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [tableRowCounts, setTableRowCounts] = useState({});
  const [scanningTables, setScanningTables] = useState(false);
  const abortControllerRef = useRef(null);
  const [dataSearch, setDataSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [activeColumnFilters, setActiveColumnFilters] = useState({});
  const searchTimerRef = useRef(null);
  const colFilterTimerRef = useRef(null);

  // Şemaları yükle
  useEffect(() => {
    loadSchemas();
  }, []);

  // Şema değiştiğinde tabloları yükle ve önceki istekleri iptal et
  useEffect(() => {
    // Önceki istekleri iptal et
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Yeni abort controller oluştur
    abortControllerRef.current = new AbortController();
    
    if (selectedSchema) {
      // State'i tamamen temizle - önceki şemadaki tüm verileri sıfırla
      setSelectedTable(null);
      setTableData(null);
      setTableStructure(null);
      setTables([]);
      setTableRowCounts({});
      setError(null); // Hata mesajını da temizle
      setCurrentPage(0);
      setTotalCount(0);
      setLoading(false);
      setScanningTables(false);
      
      // Yeni şema için tabloları yükle
      loadTables(selectedSchema);
    }
    
    // Cleanup: component unmount olduğunda istekleri iptal et
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedSchema]);

  // Tablo seçildiğinde verileri yükle
  useEffect(() => {
    if (selectedTable && selectedSchema) {
      loadTableData(selectedSchema, selectedTable, activeSearch, activeColumnFilters);
      loadTableStructure(selectedSchema, selectedTable);
    }
  }, [selectedTable, selectedSchema, currentPage, activeSearch, activeColumnFilters]);

  const loadSchemas = async () => {
    try {
      setLoading(true);
      setError(null);
      const schemasList = await databaseService.listSchemas();
      setSchemas(schemasList);
      
      // Eğer 'public' şeması varsa varsayılan olarak seç
      if (schemasList.includes('public')) {
        setSelectedSchema('public');
      } else if (schemasList.length > 0) {
        setSelectedSchema(schemasList[0]);
      }
    } catch (err) {
      console.error('Şema yükleme hatası:', err);
      setError(err.message || 'Şemalar yüklenemedi. Backend API endpoint\'ini kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  const loadTables = async (schema) => {
    try {
      setLoading(true);
      setError(null);
      
      // Önceki istekleri iptal et
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      const tablesList = await databaseService.listTables(schema);
      
      // İstek iptal edildiyse devam etme
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }
      
      setTables(tablesList);
      
      // Her tablo için kayıt sayısını al (hızlı tarama)
      scanTableRowCounts(schema, tablesList);
    } catch (err) {
      // Abort hatası ise görmezden gel
      if (err.name === 'AbortError') {
        return;
      }
      console.error('Tablo yükleme hatası:', err);
      setError(err.message || 'Tablolar yüklenemedi.');
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Tüm tabloları tarayıp kayıt sayılarını al
  const scanTableRowCounts = async (schema, tablesList) => {
    try {
      setScanningTables(true);
      const newCounts = {};
      const currentSchema = selectedSchema; // Şema değişirse kontrol için
      
      // Her tablo için kayıt sayısını al (paralel)
      const promises = tablesList.map(async (table) => {
        // Şema değiştiyse iptal et
        if (currentSchema !== selectedSchema) {
          return;
        }
        
        try {
          const data = await databaseService.getTableData(schema, table, 1, 0);
          
          // Şema hala aynı mı kontrol et
          if (currentSchema === selectedSchema) {
            newCounts[`${schema}.${table}`] = data.totalCount;
            console.log(`${schema}.${table}: ${data.totalCount} kayıt`);
          }
        } catch (err) {
          // Abort hatası ise görmezden gel
          if (err.name === 'AbortError') {
            return;
          }
          
          // Şema değiştiyse görmezden gel
          if (currentSchema !== selectedSchema) {
            return;
          }
          
          // "does not exist" hatalarını sessizce yok say (başka şemadaki tablolara istek atılmış olabilir)
          if (err.message?.includes('does not exist')) {
            // Sessizce yok say, log bile yazma
            return;
          }
          
          // Diğer hataları logla
          console.warn(`Tablo ${schema}.${table} için kayıt sayısı alınamadı:`, err.message);
          if (currentSchema === selectedSchema) {
            newCounts[`${schema}.${table}`] = -1; // Hata durumu
          }
        }
      });
      
      await Promise.all(promises);
      
      // Şema hala aynı mı kontrol et
      if (currentSchema === selectedSchema) {
        setTableRowCounts(prev => {
          // Önceki şemadaki kayıtları temizle, sadece mevcut şemayı sakla
          const cleaned = {};
          Object.keys(prev).forEach(key => {
            if (key.startsWith(`${schema}.`)) {
              cleaned[key] = prev[key];
            }
          });
          return { ...cleaned, ...newCounts };
        });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Tablo tarama hatası:', err);
      }
    } finally {
      if (selectedSchema === schema) {
        setScanningTables(false);
      }
    }
  };

  const loadTableData = async (schema, tableName, search = '', colFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      const offset = currentPage * pageSize;
      const data = await databaseService.getTableData(schema, tableName, pageSize, offset, search, colFilters);
      
      if (abortControllerRef.current?.signal.aborted || selectedSchema !== schema) {
        return;
      }
      
      setTableData(data.rows);
      setTotalCount(data.totalCount);
    } catch (err) {
      if (err.name === 'AbortError' || selectedSchema !== schema) {
        return;
      }
      
      if (err.message?.includes('does not exist')) {
        return;
      }
      
      console.error('Tablo verisi yükleme hatası:', err);
      setError(err.message || 'Tablo verileri yüklenemedi.');
    } finally {
      if (!abortControllerRef.current?.signal.aborted && selectedSchema === schema) {
        setLoading(false);
      }
    }
  };

  const loadTableStructure = async (schema, tableName) => {
    try {
      const structure = await databaseService.getTableStructure(schema, tableName);
      setTableStructure(structure);
    } catch (err) {
      console.error('Tablo yapısı yükleme hatası:', err);
    }
  };

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    setCurrentPage(0);
    setDataSearch('');
    setActiveSearch('');
    setColumnFilters({});
    setActiveColumnFilters({});
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (colFilterTimerRef.current) clearTimeout(colFilterTimerRef.current);
  };

  const handleSearchChange = (value) => {
    setDataSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(0);
      setActiveSearch(value);
    }, 500);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      setCurrentPage(0);
      setActiveSearch(dataSearch);
    }
  };

  const handleColumnFilterChange = (colName, value) => {
    setColumnFilters(prev => ({ ...prev, [colName]: value }));
    if (colFilterTimerRef.current) clearTimeout(colFilterTimerRef.current);
    colFilterTimerRef.current = setTimeout(() => {
      setCurrentPage(0);
      setActiveColumnFilters(prev => ({ ...prev, [colName]: value }));
    }, 600);
  };

  const handleColumnFilterKeyDown = (e, colName) => {
    if (e.key === 'Enter') {
      if (colFilterTimerRef.current) clearTimeout(colFilterTimerRef.current);
      setCurrentPage(0);
      setActiveColumnFilters(prev => ({ ...prev, [colName]: columnFilters[colName] || '' }));
    }
  };

  const hasActiveColumnFilters = Object.values(activeColumnFilters).some(v => v && v.trim());

  const clearAllFilters = () => {
    setDataSearch('');
    setActiveSearch('');
    setColumnFilters({});
    setActiveColumnFilters({});
    setCurrentPage(0);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(totalCount / pageSize) - 1;
    if (currentPage < maxPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className={styles.databaseViewer}>
      <div className={styles.databaseViewerHeader}>
        <div>
          <h2>Veritabanı Yönetimi</h2>
          <p className={styles.databaseViewerSubtitle}>
            PostgreSQL veritabanındaki şemalar ve tabloları görüntüleyin
          </p>
        </div>
        <button 
          className={styles.refreshButton}
          onClick={() => {
            loadSchemas();
            if (selectedSchema) {
              loadTables(selectedSchema);
            }
          }}
          disabled={loading}
        >
          Yenile
        </button>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button 
            onClick={() => setError(null)} 
            style={{ marginLeft: '12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      <div className={styles.databaseViewerContent}>
        {/* Sol panel: Şemalar ve Tablolar */}
        <div className={styles.databaseSidebar}>
          <div className={styles.databaseSection}>
            <h3>Şemalar</h3>
            <div className={styles.schemaList}>
              {schemas.map(schema => (
                <button
                  key={schema}
                  className={`${styles.schemaButton} ${selectedSchema === schema ? styles.active : ''}`}
                  onClick={() => setSelectedSchema(schema)}
                >
                  {schema}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.databaseSection}>
            <h3>Tablolar ({selectedSchema})</h3>
            {loading && tables.length === 0 ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Yükleniyor...</p>
              </div>
            ) : tables.length === 0 ? (
              <p className={styles.emptyState}>Tablo bulunamadı</p>
            ) : (
              <div className={styles.tableList}>
                {tables.map(table => {
                  const tableKey = `${selectedSchema}.${table}`;
                  const rowCount = tableRowCounts[tableKey];
                  const hasData = rowCount !== undefined && rowCount > 0;
                  
                  return (
                    <button
                      key={table}
                      className={`${styles.tableButton} ${selectedTable === table ? styles.active : ''} ${hasData ? styles.tableWithData : ''}`}
                      onClick={() => handleTableSelect(table)}
                      title={rowCount !== undefined ? `${rowCount} kayıt` : 'Kayıt sayısı yükleniyor...'}
                    >
                      <span>{table}</span>
                      {rowCount !== undefined && (
                        <span className={styles.tableRowCount}>
                          {rowCount > 0 ? `(${rowCount})` : '(boş)'}
                        </span>
                      )}
                      {scanningTables && rowCount === undefined && (
                        <span className={styles.tableRowCount}>...</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sağ panel: Tablo Verileri */}
        <div className={styles.databaseMain}>
          {!selectedTable ? (
            <div className={styles.emptyStateContainer}>
              <p>Bir tablo seçin</p>
            </div>
          ) : (
            <>
              <div className={styles.tableInfoHeader}>
                <h3>
                  {selectedSchema}.{selectedTable}
                </h3>
                {tableStructure && (
                  <span className={styles.tableInfoBadge}>
                    {tableStructure.length} kolon
                  </span>
                )}
              </div>

              {tableStructure && (
                <div className={styles.tableStructure}>
                  <h4>Tablo Yapısı</h4>
                  <div className={styles.structureGrid}>
                    {tableStructure.map((col, index) => (
                      <div key={index} className={styles.structureItem}>
                        <strong>{col.column_name}</strong>
                        <span>{col.data_type}</span>
                        {col.is_nullable === 'NO' && <span className={styles.requiredBadge}>NOT NULL</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loading ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.spinner}></div>
                  <p>Veriler yükleniyor...</p>
                </div>
              ) : error ? (
                <div className={styles.errorMessage}>{error}</div>
              ) : tableData && tableData.length > 0 ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', marginBottom: 4 }}>
                    <input
                      type="text"
                      placeholder="Tüm verilerde ara... (Enter ile arayın)"
                      value={dataSearch}
                      onChange={e => handleSearchChange(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      style={{ flex: 1, maxWidth: 360, padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none', background: '#fff', color: '#1e293b' }}
                    />
                    {activeSearch.trim() && (
                      <>
                        <span style={{ fontSize: 11, color: '#1e40af', fontWeight: 600 }}>
                          {totalCount.toLocaleString('tr-TR')} sonuç
                        </span>
                        <button onClick={clearAllFilters} style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, cursor: 'pointer', background: '#f8fafc', color: '#64748b' }}>Temizle</button>
                      </>
                    )}
                    {!activeSearch.trim() && hasActiveColumnFilters && (
                      <>
                        <span style={{ fontSize: 11, color: '#1e40af', fontWeight: 600 }}>
                          {totalCount.toLocaleString('tr-TR')} sonuç (kolon filtresi)
                        </span>
                        <button onClick={clearAllFilters} style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, cursor: 'pointer', background: '#f8fafc', color: '#64748b' }}>Tum Filtreleri Temizle</button>
                      </>
                    )}
                  </div>
                  <div className={styles.tableDataContainer}>
                    <div className={styles.tableWrapper}>
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            {tableStructure?.map((col, index) => (
                              <th key={index}>{col.column_name}</th>
                            ))}
                          </tr>
                          <tr>
                            {tableStructure?.map((col, index) => (
                              <th key={`f-${index}`} style={{ padding: '4px 3px', background: '#f8fafc' }}>
                                <input
                                  type="text"
                                  placeholder="Filtre..."
                                  value={columnFilters[col.column_name] || ''}
                                  onChange={e => handleColumnFilterChange(col.column_name, e.target.value)}
                                  onKeyDown={e => handleColumnFilterKeyDown(e, col.column_name)}
                                  style={{
                                    width: '100%', padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4,
                                    fontSize: 11, outline: 'none', background: '#fff', color: '#1e293b', boxSizing: 'border-box',
                                    ...(activeColumnFilters[col.column_name] ? { borderColor: '#3b82f6', background: '#eff6ff' } : {}),
                                  }}
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(tableData || []).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {tableStructure?.map((col, colIndex) => (
                                <td key={colIndex}>
                                  {row[col.column_name] !== null && row[col.column_name] !== undefined
                                    ? <HighlightText text={String(row[col.column_name])} query={activeColumnFilters[col.column_name] || activeSearch} />
                                    : <span className={styles.nullValue}>NULL</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                          {tableData && tableData.length === 0 && activeSearch.trim() && (
                            <tr><td colSpan={tableStructure?.length || 1} style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 12 }}>Aramayla eşleşen kayıt bulunamadı</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className={styles.pagination}>
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 0 || loading}
                      className={styles.paginationButton}
                    >
                      ← Önceki
                    </button>
                    <span className={styles.paginationInfo}>
                      Sayfa {currentPage + 1} / {totalPages || 1} (Toplam: {totalCount} kayıt)
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages - 1 || loading}
                      className={styles.paginationButton}
                    >
                      Sonraki →
                    </button>
                  </div>
                </>
              ) : (
                <div className={styles.emptyStateContainer}>
                  <p>Bu tabloda veri bulunmuyor</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseViewer;

